/**
 * Live feedback, computed from the regions and the player's marks alone (CLAUDE.md §10): the
 * answer key never reaches this code. A mark is flagged only when no continuation can fix it.
 */
import type { Feedback } from "../types";
import { resizeRows } from "./progress";
import { contradictions, regionCells, type Contradictions } from "./shading";
import { cellId, type Cell, type LitsDefinition, type LitsProgress } from "./types";

export const REGION_CONTRADICTION = "region_contradiction";
export const POOL = "pool";
export const DISCONNECTED = "disconnected";

const ids = (cells: Cell[]) => cells.map(([r, c]) => cellId(r, c));

/** The contradictions of a board, padded or cropped to the definition's size first. */
export function liveContradictions(definition: LitsDefinition, rows: string[]): Contradictions {
  return contradictions(definition.regions, resizeRows(rows, definition.rows, definition.cols));
}

/** The cells to tint: every cell of a flagged region, and every cell of a shaded 2×2 block. */
export function flaggedCells(definition: LitsDefinition, found: Contradictions): Set<string> {
  const anchors = new Set(found.regions.map(([r, c]) => `${r},${c}`));
  const out = new Set<string>();
  for (const cells of regionCells(definition.regions)) {
    if (anchors.has(`${cells[0][0]},${cells[0][1]}`))
      for (const [r, c] of cells) out.add(`${r},${c}`);
  }
  for (const [r, c] of found.pools)
    for (const [dr, dc] of [
      [0, 0],
      [0, 1],
      [1, 0],
      [1, 1],
    ])
      out.add(`${r + dr},${c + dc}`);
  return out;
}

export function localFeedback(definition: LitsDefinition, progress: LitsProgress): Feedback[] {
  const found = liveContradictions(definition, progress.rows);
  const anchors = new Set(found.regions.map(([r, c]) => `${r},${c}`));
  const feedback: Feedback[] = regionCells(definition.regions)
    .filter((cells) => anchors.has(`${cells[0][0]},${cells[0][1]}`))
    .map((cells) => ({ type: "region", code: REGION_CONTRADICTION, cells: ids(cells) }));
  for (const [r, c] of found.pools)
    feedback.push({
      type: "cell",
      code: POOL,
      cells: ids([
        [r, c],
        [r, c + 1],
        [r + 1, c],
        [r + 1, c + 1],
      ]),
    });
  if (found.disconnected) feedback.push({ type: "grid", code: DISCONNECTED, cells: [] });
  return feedback;
}
